/** Minimal shape needed by these helpers; satisfied by IGroup and IGroupWithCategory. */
interface GroupLike {
  id: string;
  title: string;
  scope: string;
  // GHG Protocol Scope 3 category number (1 to 15), null for Scope 1 and Scope 2 groups. It comes
  // from the API (`groups.scope3CategoryNumber`), which is the single source for it: the GHG,
  // Carbon Reduction Plan and SRS reports number their Scope 3 tables from the same column. It is
  // required here on purpose, so a group object built without it fails to compile instead of
  // silently losing its "Scope 3.1" prefix.
  scope3CategoryNumber: number | null;
}

/**
 * Returns the GHG category number for a group, or null for Scope 1 / Scope 2 groups.
 */
export const getGroupGhgCategoryNumber = (group: GroupLike): number | null => {
  return group.scope3CategoryNumber ?? null;
};

/**
 * Centralized dropdown label for a group.
 *
 * Default (`includeScope: true`) leads with the scope so every option lines up,
 * e.g. "Scope 1: Company Facilities" and "Scope 3.1: Purchased Goods and
 * Services" (Scope 3 categories use 3.1 to 3.15 decimal notation).
 *
 * Pass `{ includeScope: false }` where the scope is already shown elsewhere
 * (e.g. a section header): then only the GHG category number prefixes the
 * title for Scope 3 ("1. Purchased Goods and Services"), and Scope 1/2 show the
 * plain title.
 */
export const formatGroupLabel = (
  group: GroupLike,
  { includeScope = true }: { includeScope?: boolean } = {},
): string => {
  const number = getGroupGhgCategoryNumber(group);
  if (includeScope) {
    const scopeToken = number !== null ? `Scope ${group.scope}.${number}` : `Scope ${group.scope}`;
    return `${scopeToken}: ${group.title}`;
  }
  const prefix = number !== null ? `${number}. ` : '';
  return `${prefix}${group.title}`;
};

/**
 * Returns a NEW array (input not mutated) ordered by scope (1, 2, 3) then by
 * GHG category number ascending within Scope 3. Groups without a number
 * (Scope 1/2) fall back to alphabetical by title within their scope.
 * Generic so the caller's element type (e.g. IGroupWithCategory) is preserved.
 */
export const sortGroupsByGhgCategory = <T extends GroupLike>(groups: T[]): T[] => {
  return groups.slice().sort((a, b) => {
    const scopeA = a.scope || '';
    const scopeB = b.scope || '';
    if (scopeA !== scopeB) return scopeA.localeCompare(scopeB);

    const numA = getGroupGhgCategoryNumber(a);
    const numB = getGroupGhgCategoryNumber(b);
    if (numA !== null && numB !== null) return numA - numB;
    if (numA !== null) return -1;
    if (numB !== null) return 1;
    return a.title.localeCompare(b.title);
  });
};

/**
 * The 15 GHG Protocol Scope 3 categories, labelled as the reports print them (the backend's
 * SCOPE3_CATEGORY_LABELS in lib/constants/scope3Categories.constants.js). Used where a form asks for
 * figures by category rather than by group, such as an archived report period's Scope 3 breakdown.
 */
export const SCOPE3_CATEGORIES: { number: number, label: string }[] = [
  { number: 1, label: 'Purchased goods and services' },
  { number: 2, label: 'Capital goods' },
  { number: 3, label: 'Fuel- and energy-related activities' },
  { number: 4, label: 'Upstream transportation and distribution' },
  { number: 5, label: 'Waste generated in operations' },
  { number: 6, label: 'Business travel' },
  { number: 7, label: 'Employee commuting' },
  { number: 8, label: 'Upstream leased assets' },
  { number: 9, label: 'Downstream transportation and distribution' },
  { number: 10, label: 'Processing of sold products' },
  { number: 11, label: 'Use of sold products' },
  { number: 12, label: 'End-of-life treatment of sold products' },
  { number: 13, label: 'Downstream leased assets' },
  { number: 14, label: 'Franchises' },
  { number: 15, label: 'Investments' },
];
